<template>
  <v-dialog
    :value="value"
    width="500"
    persistent
    @input="$emit('input', $event)"
  >
    <v-card v-if="timecard">
      <v-card-title class="d-flex flex-wrap align-center">
        <span>{{ timecard.userName }}の申告</span>

        <v-chip
          v-if="sourceLabel"
          small
          class="ml-3 check-in-chip"
          :class="`check-in-chip--${timecard.startAtSource}`"
        >
          {{ sourceLabel }}
        </v-chip>
      </v-card-title>

      <v-card-text>
        <check-in-declarations
          v-model="declarations"
          :items="items"
          :declared="timecard.declarations || []"
          :declared-today-item-ids="declaredTodayItemIds"
          with-amount
        />

        <v-alert
          v-if="notice"
          dense
          text
          :type="notice.type"
          class="mt-3 mb-0 text-sm"
        >
          {{ notice.text }}
          <div
            v-if="notice.canReapply"
            class="mt-2"
          >
            <v-btn
              small
              outlined
              color="primary"
              :ripple="false"
              @click="$emit('reapply', timecard)"
            >
              自動の時刻({{ hhmm(calculation.startAt) }})に戻す
            </v-btn>
          </div>
        </v-alert>

        <p
          v-if="timecard.punchedAt"
          class="text-xs secondary--text mt-3 mb-0"
        >
          打刻した時刻 {{ hhmm(timecard.punchedAt) }}
        </p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn
          color="primary"
          :ripple="false"
          @click="$emit('save', { timecard, declarations })"
        >
          更新
        </v-btn>

        <v-btn
          text
          color="secondary"
          :ripple="false"
          @click="$emit('input', false)"
        >
          キャンセル
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { parseISO } from 'date-fns'
import useDateFormat from '@/views/composable/useDateFormat'
import CheckInSettingsApi from '@/api/waiter/CheckInSettings'
import useCheckInSettings from '@/views/composable/useCheckInSettings'
import CheckInDeclarations from './CheckInDeclarations.vue'

// 出勤後の申告の変更と金額の修正(日締め・データ画面のタイムカード表から開く)。
// 申告を変えたときの出勤時刻は、保存の前にサーバーで計算して見せる
export default {
  components: {
    CheckInDeclarations,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },

    // { id, userId, businessDate, userName, startAtSource, punchedAt, declarations: [{ itemId, name, amount }] }
    timecard: {
      type: Object,
      default: null,
    },

    // その店舗に当てはまる申告項目
    items: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const { hhmm } = useDateFormat()
    const { getDeclaredTodayItemIds } = useCheckInSettings()
    const declarations = ref([])
    const declaredTodayItemIds = ref([])
    const calculation = ref(null)

    const sourceLabel = computed(() => {
      return { rule: '出勤時刻は自動', manual: '出勤時刻は手修正' }[props.timecard?.startAtSource] || null
    })

    const isSameTime = (a, b) => !!a && !!b && parseISO(a).getTime() === parseISO(b).getTime()

    const notice = computed(() => {
      if (!calculation.value?.startAt) return null

      if (!calculation.value.recalculates) {
        if (props.timecard?.startAtSource !== 'manual') return null

        return {
          type: 'warning',
          text: '出勤時刻は手で直してあるため、申告を変えても変わりません。',
          canReapply: true,
        }
      }

      if (isSameTime(calculation.value.startAt, calculation.value.currentStartAt)) return null

      return {
        type: 'info',
        text: `更新すると、出勤時刻が ${hhmm(calculation.value.currentStartAt)} から ${hhmm(calculation.value.startAt)} に変わります。`,
        canReapply: false,
      }
    })

    const refresh = async () => {
      if (!props.value || !props.timecard?.punchedAt) {
        calculation.value = null

        return
      }

      try {
        const res = await CheckInSettingsApi.calculate({
          timecardId: props.timecard.id,
          declarationItemIds: declarations.value.map(declaration => +declaration.itemId),
        })
        calculation.value = res?.data || null
      } catch (error) {
        calculation.value = null
      }
    }

    watch(() => props.value, isOpen => {
      if (!isOpen) return

      declarations.value = (props.timecard?.declarations || [])
        .map(declaration => ({ itemId: declaration.itemId, amount: declaration.amount }))
      refresh()
      declaredTodayItemIds.value = []
      getDeclaredTodayItemIds({
        userId: props.timecard?.userId,
        date: props.timecard?.businessDate || null,
        exceptTimecardId: props.timecard?.id,
      }).then(ids => { declaredTodayItemIds.value = ids })
    }, { immediate: true })

    watch(declarations, refresh)

    return {
      declarations,
      declaredTodayItemIds,
      calculation,
      sourceLabel,
      notice,
      hhmm,
    }
  },
}
</script>

<style scoped>
/* Vuetify の color pack は無効のため、色は独自クラスで指定する */
.check-in-chip--rule {
  background: rgba(86, 202, 0, 0.12) !important;
  color: #3c8c00 !important;
}
.check-in-chip--manual {
  background: rgba(22, 177, 255, 0.12) !important;
  color: #0a6e9f !important;
}
</style>
